import { formatPhoneNumber, isSame } from '../../utils';
import { PDSWarning, Patient } from '../Patient';

const listToString = (list: (string | undefined)[]) =>
    list.filter(Boolean).join(', ').toLowerCase();

export default function generatePdsWarnings(patient: Patient): PDSWarning[] {
    const warnings: PDSWarning[] = [];

    if (!patient.pds?.record) {
        return warnings;
    }

    // Check if the patient's address matches one of those on the PDS record
    const patientAddressString = listToString([
        patient.addressLine1,
        patient.addressLine2,
        patient.addressLine3,
        patient.town,
        patient.county,
        patient.postCode,
    ]);

    if (
        !patient.pds.record.address.some((address) => {
            const pdsAddressString = listToString([
                address.addressLine1,
                address.addressLine2,
                address.addressLine3,
                address.town,
                address.county,
                address.postCode,
            ]);
            return pdsAddressString === patientAddressString;
        })
    ) {
        warnings.push({
            field: 'address',
            data: {
                currentValue: {
                    addressLine1: patient.addressLine1,
                    addressLine2: patient.addressLine2,
                    addressLine3: patient.addressLine3,
                    town: patient.town,
                    county: patient.county,
                    postCode: patient.postCode,
                },
                pdsValue: patient.pds.record.address,
            },
        });
    }

    // Check if the patient's name matches one of those on the PDS record
    const patientName = listToString([patient.firstName, patient.middleName, patient.lastName]);

    if (
        !patient.pds.record.name.some((name) => {
            const pdsName = listToString([name.firstName, name.middleName, name.lastName]);
            return pdsName === patientName;
        })
    ) {
        warnings.push({
            field: 'name',
            data: {
                currentValue: {
                    firstName: patient.firstName,
                    middleName: patient.middleName,
                    lastName: patient.lastName,
                },
                pdsValue: patient.pds.record.name,
            },
        });
    }

    // Check if any of the patient's phone numbers match those on the PDS record
    const patientPhoneNumbers = [
        patient.contactNumber || '',
        ...(patient.additionalContactNumbers || []),
    ]
        .filter(Boolean)
        .map(formatPhoneNumber);

    if (
        !patient.pds.record.contactNumber.some(({ value }) => patientPhoneNumbers.includes(value))
    ) {
        warnings.push({
            field: 'contactNumber',
            data: {
                currentValue: patientPhoneNumbers,
                pdsValue: patient.pds.record.contactNumber,
            },
        });
    }

    // Check if the date of birth of the patient matches the PDS record
    if (
        patient.dateOfBirth &&
        patient.pds.record.dateOfBirth &&
        !isSame(patient.pds.record.dateOfBirth, patient.dateOfBirth)
    ) {
        warnings.push({
            field: 'dateOfBirth',
            data: {
                currentValue: patient.dateOfBirth,
                pdsValue: patient.pds.record.dateOfBirth,
            },
        });
    }

    // Check if the gender of the patient matches the PDS record
    if (
        patient.gender &&
        patient.pds.record.gender.toLowerCase() !== patient.gender.toLowerCase()
    ) {
        warnings.push({
            field: 'gender',
            data: {
                currentValue: patient.gender,
                pdsValue: patient.pds.record.gender,
            },
        });
    }

    return warnings;
}
